/**
 * @fileoverview LiveQuery hook for an in-progress department-wide migration run.
 * @module system-web/hooks/useDepartmentWideRun
 *
 * Responsibilities:
 * - Subscribe to the latest department-wide parent and overlay each dataset's latest rerun attempt.
 * - Publish effective counts while preserving the historical parent document unchanged.
 *
 * Dependencies:
 * - dexie liveQuery, getSystemDb, getSystemDexieRaw, listActiveDepartmentWideRun, fetchAndSyncRuns
 *
 * Architecture:
 * - liveQuery observed function is a pure Dexie read.
 * - Poll fetchAndSyncRuns('department-wide') while a run is active (same pattern as useLegacyRunHistory).
 * - Overlay liveJob from background_jobs (same feed as the flyout) so the header is not stuck
 *   on a stale extract message while Occupancies n / total is already updating.
 *
 * @hook useDepartmentWideRun
 * Purpose: Drive MigrationProgressSidebar without apiClient in the component.
 * Returns: run | null, liveJob | null, isLoading
 * Side Effects: liveQuery subscribe + 3s poll of GET /runs?datasetKey=department-wide
 * Offline Behavior: last Dexie snapshot; poll no-ops when offline.
 */

import { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import { getSystemDb, getSystemDexieRaw } from '@firesquirrel/local-db';
import {
  fetchAndSyncRunById,
  fetchAndSyncRuns,
  listActiveDepartmentWideLiveJob,
  listActiveDepartmentWideRun,
  listChildDatasetRunsForParent,
  type DepartmentWideLiveJob,
  type MigrationRunRow,
} from '../services/legacyMigrationService';
import {
  overlayRehostFromChildRuns,
  overlayStepWithChildRuns,
  summarizeEffectiveDepartmentMigration,
  type OverlayableChildRun,
} from '../services/departmentWideStepper';

function overlayParentWithChildAttempts(
  parent: MigrationRunRow,
  attempts: Record<string, OverlayableChildRun[]>,
): MigrationRunRow {
  const steps = (parent.steps ?? []).map((step) => overlayStepWithChildRuns(step, attempts[step.adapterKey] ?? []));
  const summary = summarizeEffectiveDepartmentMigration(steps);
  const rehost = overlayRehostFromChildRuns(parent, attempts.fileMetadata ?? []);
  // RehostCounts.skipped is optional; the run row requires it, so default to 0.
  const rehostCounts = rehost.rehostCounts && { ...rehost.rehostCounts, skipped: rehost.rehostCounts.skipped ?? 0 };
  return { ...parent, steps, counts: summary.counts, rehostStatus: rehost.rehostStatus, rehostCounts };
}

export function useDepartmentWideRun(
  tenantId: string | null,
  legacyDeptId: number | null,
): { run: MigrationRunRow | null; liveJob: DepartmentWideLiveJob | null; isLoading: boolean } {
  const [run, setRun] = useState<MigrationRunRow | null>(null);
  const [liveJob, setLiveJob] = useState<DepartmentWideLiveJob | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setRun(null);
      setLiveJob(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    let subscription: { unsubscribe: () => void } | null = null;
    getSystemDb().then((db) => {
      if (cancelled) return;
      void db;
      const raw = getSystemDexieRaw();
      subscription = liveQuery(async () => {
        if (raw) void raw.table('background_jobs').toArray();
        const parent = await listActiveDepartmentWideRun(tenantId);
        const job = await listActiveDepartmentWideLiveJob(parent?._id);
        if (!parent?.steps?.length) return { row: parent, job };
        const attempts = await listChildDatasetRunsForParent(
          tenantId,
          parent.steps.map((step) => step.adapterKey),
          parent.startedAt,
          parent._id,
        );
        return { row: overlayParentWithChildAttempts(parent, attempts), job };
      }).subscribe({
        next: (snapshot) => {
          if (!cancelled) {
            setRun(snapshot.row);
            setLiveJob(snapshot.job);
            setIsLoading(false);
          }
        },
        error: () => {
          if (!cancelled) setIsLoading(false);
        },
      });
    }).catch(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [tenantId]);

  useEffect(() => {
    if (!legacyDeptId || !tenantId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      await fetchAndSyncRuns(legacyDeptId, 'department-wide');
      const parent = await listActiveDepartmentWideRun(tenantId);
      if (!parent?.steps?.length) return;
      const attempts = await listChildDatasetRunsForParent(
        tenantId,
        parent.steps.map((step) => step.adapterKey),
        parent.startedAt,
        parent._id,
      );
      const liveAttemptIds = Object.values(attempts)
        .flat()
        .filter((attempt) => attempt.status === 'running' || attempt.status === 'pending')
        .map((attempt) => attempt._id);
      const latestDocumentsAttemptId = attempts.fileMetadata?.[0]?._id;
      const refreshIds = latestDocumentsAttemptId && !liveAttemptIds.includes(latestDocumentsAttemptId)
        ? [...liveAttemptIds, latestDocumentsAttemptId]
        : liveAttemptIds;
      await Promise.all(refreshIds.map((runId) => fetchAndSyncRunById(runId)));
    };
    void poll();
    const id = setInterval(() => { void poll(); }, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [legacyDeptId, tenantId]);

  return { run, liveJob, isLoading };
}
